<script setup lang="ts">
import { useUserStore } from '@/stores/userStore';
import {useContactStore} from "@/stores/contactStore";
import { useI18n } from 'vue-i18n';
import { ref, computed } from 'vue';
import { useBodyScrollLock } from "@/composables/useBodyScrollLock";
import {useWindowSize} from "@vueuse/core";
import {useAuth0} from "@auth0/auth0-vue";
import UserInfo from "@/components/UserInfo/UserInfo.vue";
import PhoneItem from "@/components/PhoneItem/PhoneItem.vue";


const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['close']);
const closeDrawer = () => {
  emit('close');
};

const isDrawerOpen = computed(() => props.isOpen);
useBodyScrollLock(isDrawerOpen);


const { loginWithRedirect } = useAuth0();


// Reactive state for submenu
const isAboutSubmenuOpen = ref(false);
const toggleAboutSubmenu = () => {
  isAboutSubmenuOpen.value = !isAboutSubmenuOpen.value;
};

const logoutClicked = () => {
  userStore.logout();
  closeDrawer();
}

const { width } = useWindowSize();
const isMobileSmall = computed(() => width.value <= 768);

const userStore = useUserStore();
const contactStore = useContactStore();
const { t } = useI18n();
const loginEnabled = import.meta.env.VITE_LOGIN_ENABLED
console.log("loginEnabled: ", loginEnabled)
</script>

<template>
  <div v-if="props.isOpen" class="backdrop" @click="closeDrawer"></div>
  <div :class="['menu-drawer', { open: props.isOpen }]">
    <!--    ENABLE FOR LOGIN-->

        <div v-if="isMobileSmall && userStore.isLoggedIn" class="menu-header justify-between mb-8">

      <UserInfo
          :member="userStore.user"
          class="login"
      />

      <div class="cursor-pointer btnClose" type="default" @click="closeDrawer">
        <img src="@/assets/icons/svg/Close_16x16.svg" :alt="t('drawer.close')" width="16" />
      </div>
    </div>
    <div v-else class="menu-header justify-end">
      <div class="cursor-pointer btnClose" type="default" @click="closeDrawer">
        <img src="@/assets/icons/svg/Close_16x16.svg" :alt="t('drawer.close')" width="16" />
      </div>
    </div>

    <div class="menu-content">
      <!--    ENABLE FOR LOGIN-->

            <ul v-if="isMobileSmall && userStore.isLoggedIn" class="responsive-title-4">
        <li>
          <RouterLink  to="/my/trips"
                       @click="closeDrawer"
                       class="flex gap-x-1">
            <img src="@/assets/icons/svg/Luggage_24x24.svg" width="16" alt="My trips">

            {{ t('globalHeader.trips') }}
          </RouterLink >
        </li>
        <li>
          <RouterLink to="/my/crew-list"
                      @click="closeDrawer"
                      class="flex gap-x-1">
            <img src="@/assets/icons/svg/Crew_24x24.svg" width="16" alt="My crew">
            {{ t('globalHeader.crewList') }}
          </RouterLink>
        </li>
        <li>
          <RouterLink to="/my/account"
                      @click="closeDrawer"
                      class="flex gap-x-1 align-center">
            <img src="@/assets/icons/svg/Guest_16x16.svg" width="16" alt="My account">

            {{ t('globalHeader.account') }}
          </RouterLink>
        </li>
        <hr class="my-2.5"/>
      </ul>

      <ul>
        <!-- Cosmos Section -->
        <li class="menu-title responsive-title-5 font-bold">{{ t('drawer.cosmos') }}</li>
        <li>
          <RouterLink :to="`/${$i18n.locale}/`" @click="closeDrawer">
            <span class="responsive-body-medium font-normal">{{ t('drawer.home') }}</span>
          </RouterLink>
        </li>
        <li>
          <RouterLink :to="`/${$i18n.locale}/about`" @click="closeDrawer">
            <span class="responsive-body-medium font-normal"> {{ t('drawer.about') }}</span>
          </RouterLink>
        </li>

        <!-- Other Links -->
        <li>
          <RouterLink :to="`/${$i18n.locale}/why-book`" @click="closeDrawer">
            <span class="responsive-body-medium font-normal">{{ t('drawer.whybookwithus') }}</span>
          </RouterLink>
        </li>
        <li>
          <RouterLink :to="`/${$i18n.locale}/blog`" @click="closeDrawer">
            <span class="responsive-body-medium font-normal">{{ t('drawer.travelInspiration') }}</span>
          </RouterLink>
        </li>

        <hr class="my-2.5"/>

        <!-- Support Section -->
        <li class="menu-title responsive-title-5 font-bold">{{ t('drawer.support') }}</li>
        <li>
          <RouterLink :to="`/${$i18n.locale}/inquiry`" @click="closeDrawer">
            <span class="responsive-body-medium font-normal">{{ t('drawer.inquiry') }}</span>
          </RouterLink>
        </li>
        <li>
          <RouterLink :to="`/${$i18n.locale}/help`" @click="closeDrawer">
            <span class="responsive-body-medium font-normal">{{ t('drawer.help') }}</span>
          </RouterLink>
        </li>
        <li>
          <RouterLink :to="`/${$i18n.locale}/faq`" @click="closeDrawer">
            <span class="responsive-body-medium font-normal">{{ t('drawer.faqs') }}</span>
          </RouterLink>
        </li>

      </ul>
    </div>

<!--    ENABLE FOR LOGIN-->
    
    <!-- Login/Logout Section -->
    <div v-if="isMobileSmall" class="my-3 responsive-body-small">
      <RouterLink v-if="userStore.isLoggedIn" :to="`/${$i18n.locale}/`"
                  @click="logoutClicked()"
                  class="flex gap-x-1 align-center justify-start font-semibold cursor-pointer">
        <img src="@/assets/icons/svg/Log out_16x16.svg" width="16" alt="Log out">

        {{ t('globalHeader.logout') }}
      </RouterLink>
      <!--      <RouterLink v-else-if="!userStore.isLoggedIn" :to="`/${$i18n.locale}/login`" @click="closeDrawer">-->
      <!--        <span class="flex gap-x-1 font-semibold align-center justify-start cursor-pointer">{{ t('header.login') }}</span>-->
      <!--      </RouterLink>-->
      <div
          v-if="!userStore.isLoggedIn && loginEnabled === 'true'"
          @click="() => { loginWithRedirect(); closeDrawer(); }"
          class="flex gap-x-1 font-semibold align-center justify-start cursor-pointer"
      >
        {{ t('header.login') }}
      </div>
    </div>

        <!-- Phone Numbers Section -->
        <hr class="my-2.5"/>
        <div class="phone-numbers-section pt-3">
          <div class="flex flex-col gap-2 responsive-body-small text-bw-darkGrey">
            <div v-for="contact in contactStore.contactInfo" :key="contact.country">
              <PhoneItem :contact="contact"/>
            </div>
          </div>
        </div>
  </div>
</template>

<style scoped>
.backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9000;
  /* Ensure it's below the drawer but above everything else */
  transition: opacity 0.3s ease;
}
.menu-content {
  flex: 1; /* Allow the menu content to grow and take up available space */
  overflow-y: auto;
}

.menu-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: 350px;
  height: 100%;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transform: translateX(100%);
  transition: transform 0.3s ease;
  z-index: 9999;
  padding: 30px;
  overflow-y: auto;
}


.menu-drawer.open {
  transform: translateX(0);
}

.menu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 0.5rem;
  background-color: #fff;
}

ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

li {
  padding: 0.5rem;
}

a {
  text-decoration: none;
}

button {
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
}
.btnClose{
  border: 1px solid #B7B6BE;
  padding: 6px 16px;
  border-radius: 20px;
}
@media (max-width:768px) {
  .menu-drawer{
    border-radius: 20px 0 0 20px;
  }
}

.phone-numbers-section {
  padding: 16px 0;
  background-color: white;
  flex-shrink: 0; /* Prevent shrinking */
}

</style>
